import { datadogRum } from "@datadog/browser-rum";
import { currentBuildSha } from "src/lib/chunk-reload";

export type RoutePreload = [string, () => Promise<unknown>];

// Each route module co-locates its preloads with its lazy declarations,
// so adding a new lazy route naturally includes its preload entry.
import { preloads as homePreloads } from "src/pages/home";
import { preloads as agentPreloads } from "src/pages/agents/routes";
import { preloads as campaignPreloads } from "src/pages/campaigns/routes";
import { preloads as contextHubPreloads } from "src/pages/context-hub/routes";
import { preloads as lifecycleStudioPreloads } from "src/pages/lifecycle-studio/routes";
import { preloads as syncPreloads } from "src/pages/syncs";
import { preloads as modelPreloads } from "src/pages/models";
import { preloads as sequencePreloads } from "src/pages/sequences";
import { preloads as destinationPreloads } from "src/pages/destinations";
import { preloads as sourcePreloads } from "src/pages/sources";
import { preloads as extensionPreloads } from "src/pages/extensions";
import { preloads as alertingPreloads } from "src/pages/alerting";
import { preloads as connectionPreloads } from "src/pages/connections/routes";
import { preloads as audiencePreloads } from "src/pages/audiences/routes";
import { preloads as journeyPreloads } from "src/pages/journeys/routes";
import { preloads as profilePreloads } from "src/pages/profiles/routes";
import { preloads as traitPreloads } from "src/pages/traits";
import { preloads as governancePreloads } from "src/pages/governance";
import { preloads as analyticsPreloads } from "src/pages/analytics/routes";
import { preloads as dashboardPreloads } from "src/pages/dashboards/routes";
import { preloads as experimentPreloads } from "src/pages/experiments/routes";
import { preloads as metricPreloads } from "src/pages/metrics";
import { preloads as decisionEnginePreloads } from "src/pages/decision-engines";
import { preloads as idrPreloads } from "src/pages/identity-resolution";
import { preloads as settingsPreloads } from "src/pages/settings";
import { preloads as eventSyncPreloads } from "src/events/syncs";
import { preloads as eventSourcePreloads } from "src/events/sources";
import { preloads as eventDestinationPreloads } from "src/events/destinations";
import { preloads as eventFunctionPreloads } from "src/events/functions";

export const routePreloads: RoutePreload[] = [
  ...homePreloads,
  ...agentPreloads,
  ...campaignPreloads,
  ...contextHubPreloads,
  ...lifecycleStudioPreloads,
  ...syncPreloads,
  ...modelPreloads,
  ...sequencePreloads,
  ...destinationPreloads,
  ...sourcePreloads,
  ...extensionPreloads,
  ...alertingPreloads,
  ...connectionPreloads,
  ...audiencePreloads,
  ...journeyPreloads,
  ...profilePreloads,
  ...traitPreloads,
  ...governancePreloads,
  ...analyticsPreloads,
  ...dashboardPreloads,
  ...experimentPreloads,
  ...metricPreloads,
  ...decisionEnginePreloads,
  ...idrPreloads,
  ...settingsPreloads,
  ...eventSyncPreloads,
  ...eventSourcePreloads,
  ...eventDestinationPreloads,
  ...eventFunctionPreloads,
];

/**
 * Trigger a preload for a route path. Matches the first prefix in the registry.
 * The import is fire-and-forget — the browser caches the chunk for instant render.
 *
 * INVARIANT: preload entries are the RAW import thunks, never the
 * importWithRetry-wrapped factories from lazy-named.ts. Speculative
 * prefetch failures must stay silent (.catch below) and must not trigger
 * cache-reload fetches or ?ts= retry imports in the background.
 */
export function preloadRoute(path: string): void {
  for (const [prefix, load] of routePreloads) {
    if (path.startsWith(prefix)) {
      // Preloads are optional optimizations — if the chunk fails to load
      // (common on Firefox), React.lazy will retry on actual navigation.
      load().catch(() => {});
      return;
    }
  }
}

// Yield to the browser between chunk fetches so warming never competes
// with user interaction. Safari has no requestIdleCallback (fall back to
// a macrotask delay).
function nextIdle(): Promise<void> {
  return new Promise((resolve) => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => resolve(), { timeout: 10_000 });
    } else {
      setTimeout(resolve, 1_000);
    }
  });
}

let warmStarted = false;

/** Test-only: reset the one-shot latch. */
export function resetWarmForTests(): void {
  warmStarted = false;
}

/**
 * Fetch every registered route chunk, sequentially and idle-paced, so a
 * long-lived tab has the whole route graph in HTTP cache before the next
 * deploy deletes the current chunks (APP-CFS skew exposure).
 *
 * Same INVARIANT as preloadRoute: these are the raw import thunks —
 * failures stay silent (a warm miss just means the eventual navigation
 * takes the normal lazy path with its own retry ladder).
 */
export async function warmRoutePreloads(
  entries: RoutePreload[] = routePreloads,
): Promise<{ warmed: number; failed: number }> {
  if (warmStarted) {
    return { warmed: 0, failed: 0 };
  }
  warmStarted = true;

  let warmed = 0;
  let failed = 0;
  for (const [, load] of entries) {
    await nextIdle();
    try {
      await load();
      warmed += 1;
    } catch {
      failed += 1;
    }
  }

  datadogRum.addAction("chunk:warm", {
    warmed,
    failed,
    total: entries.length,
    build_sha: currentBuildSha(),
  });
  return { warmed, failed };
}
